import React, { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { ColDef } from "ag-grid-community";
import { CheckCircle2, Copy, Eye, MoreHorizontal, Search, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import PaginationLayout from "../components/PaginationLayout";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

import { formatPhone } from "../utils/phone";
interface innerData {
  name: string;
  email: string;
  phone_no: number;
  country_code?: string;
  _id: string;
  subject?: string;
  description?: string;
  message: string;
  resolved: boolean;
}

interface Contactus {
  data: Array<innerData>;
  total_count: number;
}

export default () => {
  const location = useLocation();
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);
  const limit = 10;
  const { authState, handlePagination, handleFilter, handleError } = useContext(GlobalContext);

  const [state, setState] = useState({
    data: [],
    total_count: 0,
  } as Contactus);
  const [loading, setLoading] = useState(false);
  const [readContent, setReadContent] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState<innerData | null>(null);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const initialiseData = async () => {
    try {
      setLoading(true);
      let apiRes = await henceforthApi.ContactUs.pagination(
        Number(params.page) - 1,
        newParam.toString(),
        limit
      );
      setState({
        ...state,
        ...apiRes.data,
      });
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialiseData();
  }, [params.page, newParam.get("search")]);

  const handleResolve = (id: string) => {
    const data = {
      language: "ENGLISH",
      type: "RESOLVE",
      _id: id,
    };
    henceforthApi.ContactUs.resolveContact(data)
      .then(() => {
        Swal.fire({
          title: "Contact Resolved",
          text: "Contact resolved successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
        initialiseData();
      })
      .catch((err: any) => {
        Swal.fire({
          title: "Resolution Failed",
          text: err?.response?.body?.error_description || "Failed to resolve contact",
          icon: "error",
          confirmButtonText: "Cancel",
        });
      });
  };

  const handleDelete = (id: string) => {
    const data = {
      language: "ENGLISH",
      type: "DELETE",
      _id: id,
    };
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.ContactUs.deleteContact(data);
          Swal.fire({
            title: "Contact Deleted",
            text: "Contact deleted successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          initialiseData();
        } catch (err: any) {
          Swal.fire({
            title: "Delete Failed",
            text: err?.response?.body?.error_description || "Failed to delete contact",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        }
      }
    });
  };

  const columnDefs = useMemo<ColDef<innerData>[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Name",
        field: "name",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-medium text-slate-800 text-sm">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 280,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const email = p.value;
          if (!email) return <span className="text-slate-400">Not available</span>;
          return (
            <div className="flex items-center gap-1.5 py-1 text-sm text-slate-600">
              <span className="whitespace-nowrap">{email}</span>
            </div>
          );
        },
      },
      {
        headerName: "Phone No.",
        field: "phone_no",
        minWidth: 140,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-600 text-sm">
              {p.value ? formatPhone(p.value, p.data?.country_code) : "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Message",
        field: "message",
        minWidth: 260,
        flex: 2,
        cellRenderer: (p: any) => {
          const msg = p.value || "No message";
          const isLong = msg.length > 40;
          return (
            <div className="flex items-center gap-1.5 py-1 text-sm text-slate-600">
              <span className="truncate max-w-[280px]">
                {isLong ? `${msg.slice(0, 40)}...` : msg}
              </span>
              {isLong && (
                <Button
                  type="button"
                  onClick={() => {
                    setReadContent(msg);
                    setModalOpen(true);
                  }}
                  // className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline shrink-0"
                  variant='link'
                  size='sm'
                >
                  Read more
                </Button>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "resolved",
        minWidth: 130,
        cellRenderer: (p: any) => {
          const isResolved = p.value;
          return (
            <div className="flex items-center h-full">
              {isResolved ? (
                <Badge variant="success">RESOLVED</Badge>
              ) : (
                <Badge variant="warning">PENDING</Badge>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        width: 110,
        minWidth: 100,
        maxWidth: 120,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const isResolved = row?.resolved;
          const rowId = row?._id;

          return (
            <div className="flex items-center justify-center gap-1 h-full px-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setViewItem(row)}
                aria-label="View"
                title="View"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search by email..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={state.data}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No customer messages found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state.total_count}
          data={state.data}
          page={Number(params.page)}
          limit={limit}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      {/* View Query Dialog */}
      <Dialog open={!!viewItem} onOpenChange={(open) => !open && setViewItem(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Query Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Subject</div>
              <p className="text-sm font-medium text-slate-800 break-words">
                {viewItem?.subject || "Not provided"}
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">Description</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed break-words bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                {viewItem?.description || viewItem?.message || "No message"}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              onClick={() => {
                const id = viewItem?._id;
                setViewItem(null);
                if (id) handleDelete(id);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
            {!viewItem?.resolved && (
              <Button
                type="button"
                className="gap-1.5"
                onClick={() => {
                  const id = viewItem?._id;
                  setViewItem(null);
                  if (id) handleResolve(id);
                }}
              >
                <CheckCircle2 className="w-4 h-4" />
                Resolve
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Read Message Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Message Content</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed break-words bg-slate-50 p-3.5 rounded-lg border border-slate-100">
              {readContent}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
